import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, School, ArrowRight, CalendarDays, FolderOpen, ClipboardCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateLocale } from "@/lib/i18n";
import { lessonDateLabel } from "@/pages/enseignement/AgendaTeachingPage";
import { isMediaResource, openTeachingResource, ResourcePlayerDialog } from "@/pages/enseignement/teachUtils";

// Tableau de bord de l'Espace Enseignement (enseignant : élèves + classes ; élève : ses classes)
export default function TeachingDashboard() {
  const { tspace, lang, t } = useAuth();
  const [data, setData] = useState(null);
  const [player, setPlayer] = useState(null);
  const navigate = useNavigate();
  const isTeacher = tspace?.my_role === "teacher";
  const openRes = (r) => (isMediaResource(r) ? setPlayer(r) : openTeachingResource(r));

  useEffect(() => {
    api.get("/teaching/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="t-secondary text-sm" data-testid="teach-dashboard-loading">{t("common.loading")}</p>;

  return (
    <div className="space-y-6" data-testid="teach-dashboard">
      <div className="app-card p-5" data-testid={data.next_lesson ? "next-lesson-card" : "next-lesson-empty"}>
        <p className="text-xs uppercase tracking-wider t-secondary flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5" /> {t("teach.next_lesson")}
        </p>
        {data.next_lesson ? (
          <div className="mt-1">
            <p className="font-heading font-bold capitalize">
              {lessonDateLabel(data.next_lesson, lang)}{data.next_lesson.time ? ` — ${data.next_lesson.time}` : ""}
            </p>
            <p className="text-sm t-secondary">
              {t(data.next_lesson.type === "individual" ? "teach.lesson_individual" : "teach.lesson_class")} · {data.next_lesson.target_name} · {t("teach.minutes", { n: data.next_lesson.duration_minutes })}
            </p>
            {data.next_lesson.notes && <p className="text-xs t-secondary mt-1 line-clamp-2">{data.next_lesson.notes}</p>}
          </div>
        ) : (
          <p className="text-sm t-secondary mt-1">{t("teach.no_lessons")}</p>
        )}
      </div>
      {isTeacher ? (
        <>
          <div className="grid grid-cols-2 gap-4">
            <button onClick={() => navigate("/enseignement/eleves")} className="app-card p-5 text-left hover:border-app transition-colors" data-testid="teach-students-card">
              <GraduationCap className="h-6 w-6 brand mb-2" />
              <p className="font-heading text-3xl font-bold" data-testid="teach-students-count">{data.students_count}</p>
              <p className="text-sm t-secondary">{t("teach.nav_students")}</p>
            </button>
            <button onClick={() => navigate("/enseignement/classes")} className="app-card p-5 text-left hover:border-app transition-colors" data-testid="teach-classes-card">
              <School className="h-6 w-6 brand mb-2" />
              <p className="font-heading text-3xl font-bold" data-testid="teach-classes-count">{data.classes_count}</p>
              <p className="text-sm t-secondary">{t("teach.nav_classes")}</p>
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="app-card p-5" data-testid="teach-dash-students">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold">{t("teach.nav_students")}</h3>
                <button onClick={() => navigate("/enseignement/eleves")} className="text-xs brand flex items-center gap-1 hover:underline" data-testid="teach-dash-students-all">
                  {t("teach.see_all")} <ArrowRight className="h-3 w-3" />
                </button>
              </div>
              {data.students.length === 0 ? (
                <p className="text-sm t-secondary">{t("teach.no_students")}</p>
              ) : (
                <div className="space-y-1">
                  {data.students.map((s) => (
                    <button key={s.id} onClick={() => navigate(`/enseignement/eleves/${s.id}`)} className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/5 text-left" data-testid={`teach-dash-student-${s.id}`}>
                      <span className="text-sm font-medium">{s.prenom} {s.nom}</span>
                      <span className="text-xs t-secondary">{s.instrument}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="app-card p-5" data-testid="teach-dash-classes">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold">{t("teach.nav_classes")}</h3>
                <button onClick={() => navigate("/enseignement/classes")} className="text-xs brand flex items-center gap-1 hover:underline" data-testid="teach-dash-classes-all">
                  {t("teach.see_all")} <ArrowRight className="h-3 w-3" />
                </button>
              </div>
              {data.classes.length === 0 ? (
                <p className="text-sm t-secondary">{t("teach.no_classes")}</p>
              ) : (
                <div className="space-y-1">
                  {data.classes.map((c) => (
                    <button key={c.id} onClick={() => navigate(`/enseignement/classes/${c.id}`)} className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/5 text-left" data-testid={`teach-dash-class-${c.id}`}>
                      <span className="text-sm font-medium">{c.name}</span>
                      <span className="text-xs t-secondary">{t(c.member_count > 1 ? "teach.member_count_p" : "teach.member_count_s", { n: c.member_count })}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {(data.recent_resources || []).length > 0 && (
            <div className="app-card p-5" data-testid="teach-dash-resources">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold">{t("teach.recent_resources")}</h3>
                <button onClick={() => navigate("/enseignement/ressources")} className="text-xs brand flex items-center gap-1 hover:underline" data-testid="teach-dash-resources-all">
                  {t("teach.see_all")} <ArrowRight className="h-3 w-3" />
                </button>
              </div>
              <div className="space-y-1">
                {data.recent_resources.map((r) => (
                  <button key={r.id} onClick={() => openRes(r)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 text-left" data-testid={`teach-dash-resource-${r.id}`}>
                    <FolderOpen className="h-4 w-4 brand shrink-0" />
                    <span className="text-sm font-medium truncate">{r.title}</span>
                    <span className="text-[10px] t-secondary ml-auto shrink-0">{t(`teach.type_${r.type}`)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {(data.assignments_pending || []).length > 0 && (
            <div className="app-card p-5" data-testid="teach-dash-assignments">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold">{t("teach.to_follow")}</h3>
                <button onClick={() => navigate("/enseignement/travail")} className="text-xs brand flex items-center gap-1 hover:underline" data-testid="teach-dash-assignments-all">
                  {t("teach.see_all")} <ArrowRight className="h-3 w-3" />
                </button>
              </div>
              <div className="space-y-1">
                {data.assignments_pending.map((a) => (
                  <button key={a.id} onClick={() => navigate("/enseignement/travail")} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 text-left" data-testid={`teach-dash-assignment-${a.id}`}>
                    <ClipboardCheck className="h-4 w-4 accent shrink-0" />
                    <span className="text-sm font-medium truncate">{a.title}</span>
                    <span className="text-[10px] t-secondary ml-auto shrink-0">{a.target_name} · {t("teach.done_count", { done: a.done_count, total: a.total_count })}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-4">
          <div className="app-card p-5" data-testid="teach-student-space">
            <p className="text-xs uppercase tracking-wider t-secondary">{t("teach.my_space")}</p>
            <p className="font-heading text-lg font-bold mt-1">{data.space?.name}</p>
            {data.relation?.instrument && <p className="text-sm t-secondary mt-1">{data.relation.instrument}{data.relation.level ? ` — ${data.relation.level}` : ""}</p>}
          </div>
          {(data.assignments_todo || []).length > 0 && (
            <div className="app-card p-5" data-testid="teach-student-assignments">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold">{t("teach.nav_assignments")}</h3>
                <button onClick={() => navigate("/enseignement/travail")} className="text-xs brand flex items-center gap-1 hover:underline" data-testid="teach-student-assignments-all">
                  {t("teach.see_all")} <ArrowRight className="h-3 w-3" />
                </button>
              </div>
              <div className="space-y-1">
                {data.assignments_todo.map((a) => (
                  <button key={a.id} onClick={() => navigate("/enseignement/travail")} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-white/5 text-left" data-testid={`teach-student-assignment-${a.id}`}>
                    <ClipboardCheck className="h-4 w-4 accent shrink-0" />
                    <span className="text-sm font-medium truncate">{a.title}</span>
                    <span className="text-[10px] t-secondary ml-auto shrink-0">{a.due_date || ""}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {(data.recent_resources || []).length > 0 && (
            <div className="app-card p-5" data-testid="teach-student-resources">
              <h3 className="font-heading font-bold mb-3">{t("teach.recent_resources")}</h3>
              <div className="space-y-1">
                {data.recent_resources.map((r) => (
                  <button key={r.id} onClick={() => openRes(r)} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-white/5 text-left" data-testid={`teach-student-resource-${r.id}`}>
                    <FolderOpen className="h-4 w-4 brand shrink-0" />
                    <span className="text-sm font-medium truncate">{r.title}</span>
                    <span className="text-[10px] t-secondary ml-auto shrink-0">{t(`teach.type_${r.type}`)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="app-card p-5" data-testid="teach-student-classes">
            <h3 className="font-heading font-bold mb-3">{t("teach.nav_my_classes")}</h3>
            {data.classes.length === 0 ? (
              <p className="text-sm t-secondary" data-testid="teach-student-no-class">{t("teach.no_class_student")}</p>
            ) : (
              <div className="space-y-1">
                {data.classes.map((c) => (
                  <button key={c.id} onClick={() => navigate(`/enseignement/classes/${c.id}`)} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-white/5 text-left" data-testid={`teach-student-class-${c.id}`}>
                    <School className="h-4 w-4 brand shrink-0" />
                    <span className="text-sm font-medium">{c.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {player && <ResourcePlayerDialog resource={player} onClose={() => setPlayer(null)} />}
    </div>
  );
}
